import { BossEntity }               from '../atoms/BossEntity';
import { TierBossEntity }           from '../atoms/TierBossEntity';
import { GoblinMeleeSprite }        from '../../assets/GoblinMeleeSprite';
import { GoblinSpellcasterSprite }  from '../../assets/GoblinSpellcasterSprite';

/**
 * Convert a delta vector (enemy-centre → player-centre) to a 4-way direction
 * string that is stable across small position changes — memo-friendly.
 */
function toDir(dx, dy) {
  if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return 'down';
  if (Math.abs(dy) >= Math.abs(dx)) return dy < 0 ? 'up' : 'down';
  return dx < 0 ? 'left' : 'right';
}

/**
 * Renders all enemies: regular goblins, elite goblins, spellcaster goblins,
 * mini-bosses, and tier bosses.
 *
 * Props:
 *   enemies   — array from s.enemies
 *   playerCx  — player centre X (for goblin facing direction)
 *   playerCy  — player centre Y (for goblin facing direction)
 */
export function EnemyLayer({ enemies, playerCx = 0, playerCy = 0 }) {
  return enemies.map(enemy => {
    if (enemy.isTierBoss) {
      return <TierBossEntity key={enemy.id} x={enemy.x} y={enemy.y} hp={enemy.hp} maxHp={enemy.maxHp} playerCx={playerCx} playerCy={playerCy} />;
    }
    if (enemy.isBoss) {
      return <BossEntity key={enemy.id} enemy={enemy} playerCx={playerCx} playerCy={playerCy} />;
    }

    const eSize  = enemy.isElite ? 30 : 25;
    const dmgCls = enemy.hp < enemy.maxHp ? ' enemy--damaged' : '';
    const typeCls = enemy.damageType === 'fire'      ? ' enemy--fire'
                  : enemy.damageType === 'lightning' ? ' enemy--lightning'
                  : '';

    // All goblins (melee + spellcaster) render at 2× the hitbox size, centred
    const sprSize = enemy.isElite ? 90 : 75;
    const sprOff  = (sprSize - eSize) / 2;
    const sprX    = enemy.x - sprOff;
    const sprY    = enemy.y - sprOff;

    // Direction vector: enemy centre → player centre
    const ecx = enemy.x + eSize / 2;
    const ecy = enemy.y + eSize / 2;
    const dx  = playerCx - ecx;
    const dy  = playerCy - ecy;

    if (enemy.isRanged) {
      return (
        <div
          key={enemy.id}
          className={`enemy enemy--goblin enemy--ranged${enemy.isElite ? ' enemy--elite' : ''}${dmgCls}${typeCls}`}
          style={{
            transform: `translate3d(${sprX}px,${sprY}px,0)`,
            width:  sprSize,
            height: sprSize,
          }}
        >
          {/* Spellcaster has only up/down sprites; dx passed for future expansion */}
          <GoblinSpellcasterSprite dx={dx} dy={dy} size={sprSize} />
        </div>
      );
    }

    const dir = toDir(dx, dy);

    return (
      <div
        key={enemy.id}
        className={`enemy enemy--goblin${enemy.isElite ? ' enemy--elite' : ''}${dmgCls}${typeCls}`}
        style={{
          transform: `translate3d(${sprX}px,${sprY}px,0)`,
          width:  sprSize,
          height: sprSize,
        }}
      >
        {/* Memoised goblin sprite — only re-renders when direction or size changes */}
        <GoblinMeleeSprite dir={dir} size={sprSize} />
      </div>
    );
  });
}
